'use client'

// My listings — the seller's dashboard (Roostr Web redesign Phase 6.4).
// Ported from the prototype mylistings.jsx MyListingsPage. Pure local demo:
// data lives in the zustand localStorage store (user constraint #4 — MOCK).
import * as React from 'react'
import { useRouter } from 'next/navigation'
import { HousePlus, Plus, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PillButton, REmptyState, RToastProvider, pillBtnBase, useIsMobile } from '@/components/roostr'
import { MyListingCard, useMyListingsStore } from '@/components/post'
import { useI18n } from '@/lib/i18n'

export default function MyListingsPage() {
  return (
    <RToastProvider>
      <MyListingsPageInner />
    </RToastProvider>
  )
}

function MyListingsPageInner() {
  const { t } = useI18n()
  const router = useRouter()
  const mobile = useIsMobile()
  const listings = useMyListingsStore((s) => s.listings)

  // The store rehydrates from localStorage on the client; gate rendering on
  // mount so SSR markup (seed) can't mismatch persisted state.
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => setMounted(true), [])

  const live = listings.filter((l) => l.status === 'live')
  const saves = listings.reduce((a, l) => a + (l.saves || 0), 0)
  const tips = listings.reduce((a, l) => a + (l.suggestions || []).length, 0)

  return (
    <div className={cn('mx-auto max-w-[880px]', mobile ? 'px-4 pb-14 pt-5' : 'px-8 pb-16 pt-7')}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[27px] font-bold tracking-[-0.5px]">{t('myListings.title', 'My listings')}</h1>
          <p className="mt-[3px] text-ink-secondary">
            {/* Counts come from the persisted store — render a placeholder until
                it rehydrates so SSR markup can't mismatch. */}
            {mounted ? (
              <>
                {live.length} {t('myListings.subtitleLive', 'live')} · <span className="font-mono">{saves}</span>{' '}
                {t('myListings.savesBySeekers', 'saves by seekers')}
              </>
            ) : (
              '—'
            )}
          </p>
        </div>
        <PillButton
          variant="primary"
          className="px-5 py-[11px] text-[14px]"
          onClick={() => router.push('/post')}
        >
          <Plus size={18} /> {t('myListings.postListing', 'Post a listing')}
        </PillButton>
      </div>
      {mounted && tips > 0 ? (
        <div className="mt-4 flex items-center gap-2 text-[12.5px] text-ink-secondary">
          <Sparkles size={15} className="text-primary" />
          {tips}{' '}
          {tips > 1
            ? t('myListings.assistantTipsPlural', 'things the assistant would change — each takes one tap.')
            : t('myListings.assistantTipsSingular', 'thing the assistant would change — each takes one tap.')}
        </div>
      ) : null}
      {mounted ? (
        <div className="mt-5 flex flex-col gap-6">
          {listings.map((l) => (
            <MyListingCard key={l.id} l={l} />
          ))}
        </div>
      ) : null}
      {mounted && !listings.length ? (
        <REmptyState
          icon={<HousePlus className="h-[30px] w-[30px]" />}
          title={t('myListings.emptyTitle', 'Nothing here yet')}
          text={t('myListings.emptyText', 'Post your first place — the assistant does most of the typing.')}
          action={
            <button
              type="button"
              className={cn(pillBtnBase, 'bg-primary px-[18px] py-[10px] text-[13.5px] text-white shadow-e1')}
              onClick={() => router.push('/post')}
            >
              <Plus size={16} /> {t('myListings.postListing', 'Post a listing')}
            </button>
          }
        />
      ) : null}
    </div>
  )
}
